import { inject, Injectable } from '@angular/core';
import { NgxJonasToastService } from 'ngx-jonas-toast';

import type { AithToastPayload } from './aith-toast.component';
import type { ToastOutlet, ToastRequest } from './toast-outlet';

/** Ease duration when the user allows motion; zero under reduced motion. */
const TOAST_EASE_MS = 200;

/** R-012: CSS media queries cannot reach the library's flyInOut trigger. */
function prefersReducedMotion(): boolean {
  return (
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/**
 * The production TOAST_OUTLET: NgxJonasToastService (D4), one active toast per
 * request. The facade owns the conventions and the queue; this adapter maps
 * the fully-resolved ToastRequest onto library calls and reports settlement
 * exactly once (dismiss, tap, or auto-expire), which frees the visible slot.
 */
@Injectable()
export class JonasToastOutlet implements ToastOutlet {
  private readonly toasts = inject(NgxJonasToastService);

  show(request: ToastRequest, onSettled: () => void): void {
    const message =
      request.correlationId !== undefined
        ? `${request.message} (Reference: ${request.correlationId})`
        : request.message;

    const payload: AithToastPayload = { role: request.role, ariaLive: request.ariaLive };

    const override = {
      timeOut: request.autoDismissMs ?? 0,
      disableTimeOut: request.autoDismissMs === null,
      tapToDismiss: true,
      closeButton: request.requiresDismissButton,
      positionClass: 'toast-top-right',
      newestOnTop: true,
      maxOpened: 3,
      // The R-012 motion convention: no slide/fade when the user requests
      // reduced motion (CSS alone cannot disable the Angular trigger).
      easeTime: prefersReducedMotion() ? 0 : TOAST_EASE_MS,
      payload,
    };

    const active = (() => {
      switch (request.tier) {
        case 'success':
          return this.toasts.success(message, undefined, override);
        case 'warning':
          return this.toasts.warning(message, undefined, override);
        case 'error':
          return this.toasts.error(message, undefined, override);
        default:
          return this.toasts.info(message, undefined, override);
      }
    })();

    // Exactly-once settlement: whatever closes first wins; the facade's slot
    // frees exactly once.
    let settled = false;
    const settle = (): void => {
      if (!settled) {
        settled = true;
        onSettled();
      }
    };
    active.toastRef.manualClosed().subscribe(settle);
    active.toastRef.afterClosed().subscribe(settle);
  }
}
